/*
 * Settings → Notifications: every registered type, grouped into sections,
 * with an In-App switch and an Email policy (Always / Only When Away /
 * Never), a per-row Reset to Default, plus a pause toggle. Types that share
 * a preference row show once and save together. Saves on change via
 * `PUT /api/notifications/preferences`.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ListCard, ListRow } from "@/components/ui/list-card";
import { SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

import {
  AGENT_SIGNAL_TYPE,
  AgentAlertPreferences
} from "../agentAttention/components/AgentAlertPreferences";

import { useNotifications } from "./NotificationsProvider";
import {
  buildSections,
  EMAIL_POLICY_LABELS,
  isRowDefault,
  rowPatch,
  rowValue,
  type PreferenceRow
} from "./preferencesModel";
import type { EmailPolicy, TypePreference } from "./types";

const INITIALLY_OPEN = ["clients", "other"];

const RowControls: FC<{
  row: PreferenceRow;
  value: TypePreference;
  disabled: boolean;
  onChange: (value: TypePreference) => void;
}> = ({ row, value, disabled, onChange }) => (
  <>
    {isRowDefault(row, value) ? null : (
      <Button
        variant="ghost"
        size="xs"
        disabled={disabled}
        aria-label={`Reset ${row.label} to Default`}
        onClick={() => onChange(row.defaults)}
      >
        Reset
      </Button>
    )}
    <span className="flex items-center gap-1.5">
      <Label htmlFor={`pref-${row.id}-in-app`} className="text-sm text-fg-3">
        In-App
      </Label>
      <Switch
        id={`pref-${row.id}-in-app`}
        checked={value.in_app}
        disabled={disabled}
        onCheckedChange={(v) => onChange({ ...value, in_app: v })}
      />
    </span>
    <span className="flex items-center gap-1.5">
      <Label htmlFor={`pref-${row.id}-email`} className="text-sm text-fg-3">
        Email
      </Label>
      <Select
        value={value.email}
        disabled={disabled}
        onValueChange={(v) => onChange({ ...value, email: v as EmailPolicy })}
      >
        <SelectTrigger
          id={`pref-${row.id}-email`}
          size="sm"
          className="w-36"
          aria-label={`Email for ${row.label}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(EMAIL_POLICY_LABELS) as EmailPolicy[]).map((p) => (
            <SelectItem key={p} value={p}>
              {EMAIL_POLICY_LABELS[p]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </span>
  </>
);

const Section: FC<{
  id: string;
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}> = ({ id, label, count, open, onToggle, children }) => {
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <section className="flex flex-col gap-3" data-testid={`pref-group-${id}`}>
      <SectionHeader
        title={
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            className="flex items-center gap-1.5 rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <Chevron className="size-3 text-fg-3" />
            {label}
            <span className="font-normal text-fg-4 tabular-nums">{count}</span>
          </button>
        }
      />
      {open ? <ListCard>{children}</ListCard> : null}
    </section>
  );
};

export const NotificationPreferences: FC = () => {
  const { preferences, loadPreferences, updatePreferences } =
    useNotifications();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [open, setOpen] = useState<string[]>(INITIALLY_OPEN);

  useEffect(() => {
    loadPreferences().catch(() =>
      setError("Couldn't load your notification preferences.")
    );
  }, [loadPreferences]);

  const save = async (
    key: string,
    patch: Parameters<typeof updatePreferences>[0]
  ) => {
    setSaving(key);
    try {
      await updatePreferences(patch);
    } catch {
      toast.error("Couldn't save that preference.");
    } finally {
      setSaving(null);
    }
  };

  if (error) {
    return (
      <p className="px-6 py-4 text-sm text-danger" role="alert">
        {error}
      </p>
    );
  }
  if (!preferences) {
    return (
      <div
        className="flex flex-col gap-3 px-6 py-6"
        data-testid="prefs-loading"
      >
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const agentSignal = preferences.types.find(
    (t) => t.key === AGENT_SIGNAL_TYPE
  );
  const agentSignalPref = preferences.per_type[AGENT_SIGNAL_TYPE] ?? {
    in_app: agentSignal?.default_in_app ?? true,
    email: agentSignal?.default_email ?? "always"
  };
  const sections = buildSections(preferences, [AGENT_SIGNAL_TYPE]).filter(
    (s) => s.rows.length > 0 || (s.id === "agents" && agentSignal)
  );
  const toggle = (id: string) =>
    setOpen((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );

  return (
    <div
      className="flex w-full flex-col gap-6 px-6 pt-4 pb-8"
      data-testid="notification-preferences"
    >
      <ListCard>
        <ListRow
          title={
            <Label htmlFor="pref-quiet" className="text-base text-fg">
              Pause All Notifications
            </Label>
          }
          description={
            <span className="text-sm text-fg-3">
              Nothing is delivered in-app or by email while this is on.
            </span>
          }
          control={
            <Switch
              id="pref-quiet"
              checked={preferences.quiet}
              disabled={saving === "quiet"}
              onCheckedChange={(v) => void save("quiet", { quiet: v })}
            />
          }
        />
      </ListCard>
      {sections.map((s) => {
        const agents = s.id === "agents" && agentSignal;
        return (
          <Section
            key={s.id}
            id={s.id}
            label={s.label}
            count={s.rows.length + (agents ? 1 : 0)}
            open={open.includes(s.id)}
            onToggle={() => toggle(s.id)}
          >
            {agents ? (
              <AgentAlertPreferences
                email={agentSignalPref.email !== "never"}
                emailDisabled={
                  saving === AGENT_SIGNAL_TYPE || preferences.quiet
                }
                onEmailChange={(on) =>
                  void save(AGENT_SIGNAL_TYPE, {
                    per_type: {
                      [AGENT_SIGNAL_TYPE]: {
                        in_app: agentSignalPref.in_app,
                        email: on ? "always" : "never"
                      }
                    }
                  })
                }
              />
            ) : null}
            {s.rows.map((row) => {
              const value = rowValue(row, preferences.per_type);
              return (
                <ListRow
                  key={row.id}
                  data-testid={`pref-${row.id}`}
                  title={row.label}
                  description={
                    row.description ? (
                      <span className="text-sm text-fg-3">
                        {row.description}
                      </span>
                    ) : null
                  }
                  control={
                    <RowControls
                      row={row}
                      value={value}
                      disabled={saving === row.id || preferences.quiet}
                      onChange={(next) =>
                        void save(row.id, { per_type: rowPatch(row, next) })
                      }
                    />
                  }
                />
              );
            })}
          </Section>
        );
      })}
    </div>
  );
};
